import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Seo } from '@/components/common/Seo';
import { FadeIn } from '@/components/common/FadeIn';
import { SectionLabel } from '@/components/common/SectionLabel';
import { PageHero } from '@/components/common/PageHero';
import { OphirButton } from '@/components/common/OphirButton';
import { HowItWorks } from '@/components/b2b/HowItWorks';
import { WhyOphir } from '@/components/b2b/WhyOphir';
import { SOLUTIONS, B2B_CATEGORIES, DISCLAIMER, MARKETPLACE_NOTE } from '@/data/b2b';

export default function SolutionPage({ slug }) {
  const p = SOLUTIONS[slug];
  if (!p) return null;
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: window.location.origin + '/' },
      { '@type': 'ListItem', position: 2, name: p.label, item: window.location.origin + p.path },
    ],
  };
  return (
    <>
      <Seo title={p.seoTitle} description={p.seoDescription} jsonLd={jsonLd} jsonLdId="solution-jsonld" />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-ophir-ivory-soft border-b border-ophir-grey">
        <div className="container-ophir py-3 text-[13px] text-ophir-muted">
          <Link to="/" className="hover:text-ophir-navy">Home</Link>
          <span className="mx-2 text-ophir-grey-med">/</span>
          <span className="text-ophir-navy">{p.label}</span>
        </div>
      </nav>

      <PageHero label={p.label} title={p.h1} intro={p.intro} image={p.image} imageAlt={p.imageAlt} />

      {/* Persistent RFQ ribbon */}
      <div className="bg-white border-b border-ophir-grey">
        <div className="container-ophir py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <p className="text-[15px] text-ophir-navy font-medium">Arriving with a purchasing list? Send it to our procurement team.</p>
          <div className="flex gap-3">
            <OphirButton to={p.primaryCta.to} className="px-5 py-2.5 text-[14px]">{p.primaryCta.label}</OphirButton>
            <OphirButton variant="secondary" to={p.secondaryCta.to} className="px-5 py-2.5 text-[14px]">{p.secondaryCta.label}</OphirButton>
          </div>
        </div>
      </div>

      {/* Checklist (b2b) */}
      {p.subheading && (
        <section className="bg-white">
          <div className="container-ophir py-14 lg:py-20">
            <FadeIn className="max-w-2xl">
              <SectionLabel>{p.subheading}</SectionLabel>
              <p className="mt-5 text-[16.5px] leading-relaxed text-ophir-muted">{p.subbody}</p>
            </FadeIn>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {p.checklist.map((c) => (
                <div key={c} className="flex items-center gap-3 rounded-xl border border-ophir-grey bg-ophir-ivory-soft px-4 py-3">
                  <Check size={18} className="text-ophir-gold-deep shrink-0" />
                  <span className="text-[15px] text-ophir-navy">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Who we serve / supply formats / categories list */}
      {p.who && (
        <section className={`${p.subheading ? 'bg-ophir-ivory-soft border-t border-ophir-grey' : 'bg-white'}`}>
          <div className="container-ophir py-14 lg:py-20">
            <FadeIn className="max-w-2xl">
              <SectionLabel>{p.whoTitle || 'Who we serve'}</SectionLabel>
              {p.featuresTitle && <h2 className="mt-5 font-serif text-[26px] sm:text-[32px] text-ophir-navy-deep">{p.whoTitle}</h2>}
            </FadeIn>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {p.who.map((w) => (
                <span key={w} className="rounded-full border border-ophir-grey bg-white px-4 py-2 text-[14px] text-ophir-navy shadow-ophir">{w}</span>
              ))}
            </div>
            {p.features && (
              <div className="mt-10">
                <SectionLabel>{p.featuresTitle}</SectionLabel>
                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {p.features.map((f) => (
                    <div key={f} className="flex items-center gap-3 rounded-xl border border-ophir-grey bg-white px-4 py-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-ophir-gold shrink-0" />
                      <span className="text-[15px] text-ophir-navy">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Product categories cards (b2b bulk) */}
      {p.showCategories && (
        <section className="bg-ophir-ivory-soft border-t border-ophir-grey">
          <div className="container-ophir py-16 lg:py-24">
            <FadeIn className="max-w-2xl"><SectionLabel>Product categories</SectionLabel>
              <h2 className="mt-5 font-serif text-[28px] sm:text-[36px] text-ophir-navy-deep">Multi-category sourcing</h2></FadeIn>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {B2B_CATEGORIES.map((c) => (
                <div key={c} className="rounded-2xl border border-ophir-grey bg-white p-5 shadow-ophir">
                  <div className="gold-rule w-8 mb-3" />
                  <p className="font-serif text-[16px] text-ophir-navy-deep leading-snug">{c}</p>
                </div>
              ))}
            </div>
            {p.categoriesNote && <p className="mt-6 text-[13.5px] text-ophir-muted">{p.categoriesNote}</p>}
          </div>
        </section>
      )}

      <HowItWorks />
      <WhyOphir />

      {/* Disclaimer */}
      <section className="bg-ophir-ivory-soft border-t border-ophir-grey">
        <div className="container-ophir py-10">
          <p className="text-[13px] leading-relaxed text-ophir-muted">{p.note || DISCLAIMER}</p>
          {p.marketplaceNote && <p className="mt-2 text-[13px] leading-relaxed text-ophir-muted">{MARKETPLACE_NOTE}</p>}
        </div>
      </section>

      {/* Related sectors */}
      {p.related && p.related.length > 0 && (
        <section className="bg-white border-t border-ophir-grey">
          <div className="container-ophir py-16">
            <SectionLabel>Related solutions</SectionLabel>
            <div className="mt-8 grid sm:grid-cols-3 gap-6">
              {p.related.map((slug2) => {
                const r = slug2 === 'institutional-buyers'
                  ? { path: '/institutional-buyers', label: 'Institutional Buyers', h1: 'Wholesale & procurement for institutional buyers' }
                  : SOLUTIONS[slug2];
                if (!r) return null;
                return (
                  <Link key={slug2} to={r.path} className="group block rounded-2xl border border-ophir-grey bg-ophir-ivory-soft p-6 shadow-ophir hover:shadow-ophir-hover transition-shadow">
                    <h3 className="font-serif text-[18px] text-ophir-navy-deep">{r.label}</h3>
                    <span className="mt-3 inline-flex items-center gap-2 text-[14px] font-semibold text-ophir-navy group-hover:text-ophir-navy-deep">Learn more <ArrowRight size={16} /></span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="navy-gradient">
        <div className="container-ophir py-16 text-center">
          <FadeIn>
            <h2 className="font-serif text-[28px] sm:text-[36px] text-white max-w-2xl mx-auto leading-tight">Send us your requirements and we’ll review sourcing options</h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <OphirButton variant="gold" to={p.primaryCta.to}>{p.primaryCta.label}</OphirButton>
              <OphirButton variant="secondary" to="/contact" className="!bg-transparent !border-white/50 !text-white hover:!bg-white/10">Contact Our Procurement Team</OphirButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
